import { BaseService } from './base.service';
import type {
  AssistantEmptyStateDto,
  AssistantEmptyStateParams,
  AssistantLocationSettings,
  AssistantMessageFeedback,
  AssistantSessionDetail,
  AssistantSessionDto,
  AssistantSessionList,
  AssistantStatusDto,
  AssistantUiMessageDto,
  PendingActionCardDto,
  ScopeType,
} from '@/types/support-assistant';

export class SupportAssistantService extends BaseService {
  constructor() {
    super('/support-assistant');
  }

  getStatus(locationId?: string): Promise<AssistantStatusDto> {
    const qs = locationId
      ? `?locationId=${encodeURIComponent(locationId)}`
      : '';
    return this.get<AssistantStatusDto>(`/status${qs}`);
  }

  setKillSwitch(active: boolean): Promise<AssistantStatusDto> {
    return this.patch<AssistantStatusDto>('/settings/kill-switch', { active });
  }

  markSpotlightSeen(): Promise<{ gopherSpotlightSeen: boolean }> {
    return this.patch<{ gopherSpotlightSeen: boolean }>(
      '/onboarding/spotlight-seen',
      {},
    );
  }

  setLocationEnablement(
    settings: AssistantLocationSettings,
  ): Promise<AssistantStatusDto> {
    return this.patch<AssistantStatusDto>('/settings/locations', settings);
  }

  createSession(scope?: {
    scopeType?: ScopeType;
    scopeId?: string;
  }): Promise<AssistantSessionDto> {
    return this.post<AssistantSessionDto>('/sessions', scope ?? {});
  }

  listSessions(page = 1, limit = 20): Promise<AssistantSessionList> {
    return this.get<AssistantSessionList>(
      `/sessions?page=${page}&limit=${limit}`,
    );
  }

  getSession(id: string): Promise<AssistantSessionDetail> {
    return this.get<AssistantSessionDetail>(`/sessions/${id}`);
  }

  getEmptyState(
    params: AssistantEmptyStateParams,
  ): Promise<AssistantEmptyStateDto> {
    const q = new URLSearchParams({ scopeType: params.scopeType });
    if (params.scopeType === 'module') {
      if (params.moduleKey) q.set('moduleKey', params.moduleKey);
      if (params.pagePath) q.set('pagePath', params.pagePath);
    } else if (params.scopeType !== 'none' && params.scopeId) {
      q.set('scopeId', params.scopeId);
    }
    return this.get<AssistantEmptyStateDto>(`/empty-state?${q.toString()}`);
  }

  sendHqMessage(sessionId: string, text: string): Promise<AssistantUiMessageDto> {
    return this.post<AssistantUiMessageDto>(`/sessions/${sessionId}/hq-messages`, {
      text,
    });
  }

  closeTicket(
    ticketId: string,
    note?: string,
  ): Promise<{ ticketNumber: string; closedAt: string }> {
    return this.post<{ ticketNumber: string; closedAt: string }>(
      `/tickets/${ticketId}/close`,
      note ? { note } : {},
    );
  }

  approve(id: string): Promise<PendingActionCardDto> {
    return this.post<PendingActionCardDto>(`/pending-actions/${id}/approve`);
  }

  reject(id: string): Promise<PendingActionCardDto> {
    return this.post<PendingActionCardDto>(`/pending-actions/${id}/reject`);
  }

  setMessageFeedback(
    messageId: string,
    feedback: AssistantMessageFeedback | null,
  ): Promise<{ id: string; feedback: AssistantMessageFeedback | null }> {
    return this.patch<{ id: string; feedback: AssistantMessageFeedback | null }>(
      `/messages/${messageId}/feedback`,
      { feedback },
    );
  }
}

export const supportAssistantService = new SupportAssistantService();
